import { computeInvoicePackagingCostAndPrice } from '@commons/utils/packagings';

// Composition costs are expressed per composition unit ('g', 'mL', 'unit'), packagings per
// supplier product unit ('kg', 'L', 'unit')
const COMPOSITION_UNIT_TO_PACKAGING_UNIT_FACTORS = {
  g: { kg: 1000 },
  mL: { L: 1000 },
};

/**
 * The composition's cost is expressed per composition unit and is shared across every central
 * kitchen; only the supplier product (price, packagings) differs from one central kitchen to
 * another.
 */
const getCompositionUnitCost = (composition, defaultChannelId) => {
  if (composition.isIngredient) {
    return composition.cost ?? null;
  }

  return composition.costByChannelId?.[defaultChannelId] ?? null;
};

/**
 * Converts the composition unit cost into the master packaging unit of one supplier product.
 * Units needing a custom conversion (e.g. 'unit' vs 'kg') are not computable from the cost
 * alone, so the cost stays unknown rather than wrong.
 */
const getCostInMasterPackagingUnit = (costInCompositionUnit, compositionUnit, supplierProduct) => {
  if (costInCompositionUnit == null) {
    return null;
  }

  const masterPackaging = (supplierProduct.packagings || []).find(
    ({ isActive, masterSupplierProductPackagingId }) =>
      isActive && !masterSupplierProductPackagingId,
  );

  if (!masterPackaging?.unit || masterPackaging.unit === compositionUnit) {
    return costInCompositionUnit;
  }

  const factor =
    COMPOSITION_UNIT_TO_PACKAGING_UNIT_FACTORS[compositionUnit]?.[masterPackaging.unit];

  return factor != null ? costInCompositionUnit * factor : null;
};

/**
 * Builds cost/price/margin/ratio metrics per central kitchen, keyed by supplier product id.
 * All figures are expressed on each supplier product's invoice packaging.
 */
export const computeCentralKitchenMetrics = ({
  composition,
  defaultChannelId,
  supplierProducts,
}) => {
  const costInCompositionUnit = getCompositionUnitCost(composition, defaultChannelId);

  return supplierProducts.reduce((metricsBySupplierProductId, supplierProduct) => {
    const costInMasterUnit = getCostInMasterPackagingUnit(
      costInCompositionUnit,
      composition.unit,
      supplierProduct,
    );

    const { costExTax, priceExTax } = computeInvoicePackagingCostAndPrice(
      costInMasterUnit,
      supplierProduct,
    );

    const hasCostAndPrice = costExTax != null && !!priceExTax;

    metricsBySupplierProductId[supplierProduct.id] = {
      costExTax,
      priceExTax,
      marginExTax: hasCostAndPrice ? priceExTax - costExTax : null,
      margin: hasCostAndPrice ? ((priceExTax - costExTax) / priceExTax) * 100 : null,
      ratio: hasCostAndPrice ? (costExTax / priceExTax) * 100 : null,
    };

    return metricsBySupplierProductId;
  }, {});
};
